import { useRef, useState, useEffect, useMemo } from 'react';
import { AuthContext } from "./AuthWrapper";
import { toast } from "react-hot-toast";
import { Appointment, NotesWithAuthor } from '../api/client';
import { useOutletContext } from "react-router-dom";
import { ContentState, convertFromHTML, convertToRaw } from "draft-js";
import ConfirmationModal from '../components/ConfirmModal';
import { RichTextEditor, RichTextEditorRef } from './RichTextEditor/RichTextEditor';
import { StudentNoteElement } from './StudentNoteElement';
import { StudentAppointmentElement } from './StudentAppointmentElement';
import { CaretDownFill, CaretUpFill } from 'react-bootstrap-icons';
import { Button } from './UiComponents/button';

interface StudentProps {
  studentId: string | undefined;
  setNonce: (nonce: number) => void; 
  nonce: number;
}

// Mixed type to have both Appointments and Notes in one array
type AppointmentNote = Appointment | NotesWithAuthor

// Check if mixed type is an appointment or not (useful for when we map the array)
const isAppointment = (obj: AppointmentNote): obj is Appointment => {
  return (obj instanceof Appointment);
}

// Determine what date value to actually check when we sort
// If displaying a note, this should provide the modified time since it will be the most recent change
const getSortTime = (item: AppointmentNote): number => {
  // If an appointment, just use the created time
  if(isAppointment(item)) {
    return item.createdAt?.toMillis() ?? 0;
  }

  // If a note, use the modifiedAt time; if none exists then use createdAt.
  return (
    item.modifiedAt?.toMillis() ??
    item.createdAt?.toMillis() ??
    0
  );
}

export default function StudentNotes({ studentId, setNonce, nonce }: StudentProps) {
  const [loading, setLoading] = useState<boolean>(false)
  const { client, state }: AuthContext = useOutletContext();
  const [notes, setNotes] = useState<NotesWithAuthor[]>([]);
  const noteEditorRef = useRef<RichTextEditorRef>();
  const containerRichTextEditor = useRef<HTMLDivElement>(null);
  const [editButton, setEditButton] = useState<boolean>(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [noteId, setNoteId] = useState<string | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [orderAscending, setOrderAscending] = useState<boolean>(false);


  useEffect(() => {
    setLoading(true);
    fetchNotes(studentId?.toString() || '');
    fetchAppointments(studentId?.toString() || '');
  }, [nonce]);

  // create and sort notes + appointments list by ascending/descending order
  const notesAndAppointments: AppointmentNote[] = useMemo(() => {
    return [...notes, ...appointments].sort((a, b) => {
      if(orderAscending)
        // oldest first
        return getSortTime(a) - getSortTime(b);

      // newest first
      return getSortTime(b) - getSortTime(a);
    });
  }, [notes, appointments, orderAscending]);

  const fetchNotes = async (studentId: string) => {
    if (!studentId || !client) {
      console.error('Missing required data for fetching notes');
      setLoading(false);
      return;
    }

    client.admin_GetNotes(studentId)
      .then((notes) => {
        setNotes(notes);
      })
      .catch((err) => {
        console.error('Failed to fetch notes:', err);
        toast.error('Failed to fetch notes: ' + err.message, { duration: 5000 });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchAppointments = async (studentId: string) => {
    if (!studentId || !client) {
      console.error("missing data for fetching appointments");
      setLoading(false);
      return;
    }

    client.admin_GetStudentAppointments(studentId)
    .then((appointments) => {
      setAppointments(appointments);
    }).catch((err) => {
      console.error("Failed to fetch appointments:" , err);
    })
    .finally(() => {
      setLoading(false);
    })
  }

  const scrollToRichTextEditor = async (text: string, noteId: string) => {
    setEditButton(true);
    setEditingNoteId(noteId);
    containerRichTextEditor.current?.scrollIntoView({ behavior: 'smooth' });

    const textContent = ContentState.createFromBlockArray(
      convertFromHTML(text).contentBlocks,
      convertFromHTML(text).entityMap
    );
    noteEditorRef.current?.setContentState(convertToRaw(textContent))
  };

  const scrollBackToNote = async () => {
    setLoading(false);
    noteEditorRef.current?.setText("");
    setEditButton(false);
    if (editingNoteId) {
      const noteElement = document.getElementById(`note-${editingNoteId}`);
      noteElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setEditingNoteId(null);
    }
  };

  const editNote = async () => {
    setLoading(true);
    const toastId = toast.loading("Saving note");

    // Replace the previous empty check with isEmpty()
    if (noteEditorRef.current?.isEmpty()) {
      toast.error("Note cannot be empty");
      setLoading(false);
      toast.dismiss(toastId);
      return;
    }

    if(editingNoteId == null) {
      toast.error("Could not find note to edit")
      return;
    }

    client?.admin_EditNote(editingNoteId?.toString(), noteEditorRef.current?.getText()).then(() => {
      noteEditorRef.current?.setText("");
      setNonce(Date.now());
      toast.success("Successfully edited note", { id: toastId, duration: 3500 });
      scrollBackToNote()

    }).finally(() => {
      setLoading(false);
      toast.dismiss(toastId);
    }
    )
  }

  const addNote = async () => {
    console.error(client, studentId, state.user?.id)
    if (!client || !studentId || !state.user?.id || !noteEditorRef.current) {
      toast.error("Missing required data");
      return;
    }

    // Replace the previous text check with isEmpty()
    if (noteEditorRef.current.isEmpty()) {
      toast.error("Note cannot be empty");
      return;
    }

    const noteText = noteEditorRef.current.getText();
    const toastId = toast.loading("Saving note");

    client.admin_AddNote(state.user.id, studentId, noteText)
      .then(() => {
        noteEditorRef.current?.setText("");
        setNonce(Date.now());
        toast.success("Successfully added note", { id: toastId, duration: 3500 });
      })
      .catch((error) => {
        console.error('Failed to add note:', error);
        toast.error("Could not add note", { id: toastId, duration: 4000 });
      }).finally(() => {
        setLoading(false);
        toast.dismiss(toastId);
      });
  };

  const deleteNote = async (noteId: string) => {
    if (!client || !studentId || !state.user?.id || !noteEditorRef.current) {
      toast.error("Missing required data");
      return;
    }
    const toastId = toast.loading("Saving note");
    client.admin_DeleteNote(noteId)
      .then(() => {
        setEditButton(false);
        noteEditorRef.current?.setText("");
        setNonce(Date.now());
        toast.success("Successfully deleted note", { id: toastId, duration: 3500 });
      })
      .catch((error) => {
        console.error('Failed to delete note:', error);
        toast.error("Could not delete note", { id: toastId, duration: 4000 });
      });
  };

  return (

    <>
      {loading && <div>Loading...</div>}
      <div className="w-full border-2 p-8 mt-8">
        <div className='flex justify-between items-center align-middle mb-4'>
        <h2 className="font-semibold">Notes & Appointments</h2>
        <Button
          className='gap-2'
          onClick={() => {setOrderAscending(!orderAscending)}}
          abbr={orderAscending ? "Click to change display order of notes: show newest first" : "Click to change display order of notes: show oldest first"}
        >
          <span>{orderAscending ? 'Showing oldest first' : 'Showing newest first'} </span>
          {orderAscending ? <CaretDownFill className='mt-1 w-4 h-4'/> :  <CaretUpFill className='mt-1 w-4 h-4'/>}
        </Button>
        </div>
        {notesAndAppointments && notesAndAppointments.length > 0 
        ? (notesAndAppointments.map((item) => (
            <div key={item.createdAt?.toString()}>{isAppointment(item) ? <StudentAppointmentElement app={item}/> : <StudentNoteElement note={item} scrollToRichTextEditor={scrollToRichTextEditor} setIsOpen={setIsOpen} setNoteId={setNoteId}/>}</div>
        ))) 
        : <p>No Data Available</p>}
        <div className={""} ref={containerRichTextEditor}>
          <RichTextEditor ref={noteEditorRef} simple={true} />
          {editButton ? (
            <div className='flex flex-row gap-2 mt-4'>
              <Button
                variant="default"
                onClick={editNote}
                abbr="Click to save changes to this note"
              >
                Save
              </Button>
              <Button
                variant="destructive"
                onClick={scrollBackToNote}
                abbr="Click to cancel changes to this note"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="default"
              className='mt-4'
              onClick={addNote}
              abbr="Click to create note for this student"
            >

              Add Note
            </Button>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onConfirm={deleteNote}
        Id={noteId || ''}
        TextMessage="Are you sure you want to delete this note?"
        abbrCancel="Don't delete this note"
        abbrConfirm='Delete this note'
      />
    </>
  );
}
